import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import api from '../../utils/api';

const COLORS = ['#d4a053', '#67e8f9', '#10b981', '#f59e0b', '#ef4444', '#b8860b', '#ec4899', '#14b8a6'];

export default function AnalyticsPage() {
  const [statsData, setStatsData] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalClasses: 0,
    attendanceRate: 0
  });

  const [chartData, setChartData] = useState({
    studentsByClass: [],
    genderDistribution: []
  });

  const [loading, setLoading] = useState(false);

  const loadAnalytics = async () => {
    setLoading(true);

    try {
      const studentsResponse = await api.get('/students/stats');
      const studentStats = studentsResponse.data || {};

      const teachersResponse = await api.get('/teachers/stats');
      const teacherStats = teachersResponse.data || {};

      const attendanceResponse = await api.get('/attendance/stats');
      const attendanceStats = attendanceResponse.data || {};

      const todayStats = Array.isArray(attendanceStats.todayStats) ? attendanceStats.todayStats : [];
      const presentToday = todayStats.find(s => s.status === 'present')?.count || 0;
      const totalToday = todayStats.reduce((s, row) => s + (Number(row.count) || 0), 0);
      const attendanceRate = totalToday > 0 ? Math.round((presentToday / totalToday) * 1000) / 10 : 0;

      const studentsByClass = studentStats.byClass || [];
      const genderDistribution = studentStats.genderDistribution || [];

      setStatsData({
        totalStudents: studentStats.total || 0,
        totalTeachers: teacherStats.total || 0,
        totalClasses: Array.isArray(studentsByClass) ? studentsByClass.length : 0,
        attendanceRate
      });

      setChartData({
        studentsByClass: Array.isArray(studentsByClass) ? studentsByClass : [],
        genderDistribution: Array.isArray(genderDistribution) ? genderDistribution : []
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      setChartData({
        studentsByClass: [],
        genderDistribution: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  return (
    <div style={{ padding: '20px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px', color: '#1f2937' }}>
          Analytics Dashboard
        </h1>
        <p style={{ color: '#6b7280' }}>Overview of school performance and metrics</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading analytics data...
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${COLORS[0]}`
              }}
            >
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>
                TOTAL STUDENTS
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS[0] }}>
                {statsData.totalStudents}
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                Active students
              </p>
            </div>

            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${COLORS[1]}`
              }}
            >
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>
                TOTAL TEACHERS
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS[1] }}>
                {statsData.totalTeachers}
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                Teaching staff
              </p>
            </div>

            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${COLORS[2]}`
              }}
            >
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>
                CLASSES
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS[2] }}>
                {statsData.totalClasses}
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                With enrolled students
              </p>
            </div>

            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${COLORS[3]}`
              }}
            >
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>
                ATTENDANCE RATE
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS[3] }}>
                {statsData.attendanceRate.toFixed(1)}%
              </p>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                Today's average
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                Students by Class
              </h3>
              {chartData.studentsByClass.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.studentsByClass}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="class" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                    />
                    <Bar dataKey="count" fill={COLORS[0]} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                  No data available
                </div>
              )}
            </div>

            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                Gender Distribution
              </h3>
              {chartData.genderDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.genderDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {chartData.genderDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                  No data available
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
